/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2017, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'./BaseProvider',
	'../../vipr/VIPR',
	'../../vipr/VIPRUtils',
	'../../vipr/VIPRConfig',
	'../../vipr/VIPRLibraries',
	'../../vipr/properties/PropertiesCreatorFromVisDefinition',
	'../../../lib/@waca/dashboard-common/dist/utils/ObjectHelper',
	'../../../widgets/livewidget/nls/StringResources',
	'../../../widgets/livewidget/util/VisUtil'
], function(
	_,
	BaseProvider,
	VIPR,
	VIPRUtils,
	VIPRConfig,
	VIPRLibraries,
	PropertiesCreatorFromVisDefinition,
	ObjectHelper,
	StringResources,
	VisUtil
) {
	'use strict';

	const VIPR_RENDERER = 'dashboard-analytics/visualizations/renderer/vipr3/VIPRView';
	const VIPR3Provider = BaseProvider.extend({
		init: function() {
			VIPR3Provider.inherited('init', this, arguments);
			this.viprdefinitionsPromises = null;
			this.createDefinitionsMap = {};
			this.loadedVipr3Bundles = {};

			this.initPromise = VIPR.initialize({ 'dashboardApi': this.dashboardApi, ajax: this.ajax, preFetch: this.preFetch });
		},

		contains: function(id) {
			return this.initPromise.then(()=>{
				var libs = VIPR.getLibraries();
				return Promise.resolve(!!libs[id]);
			});
		},

		clear: function(libType = VIPRLibraries.LIB_TYPE_ALL) {

			if (libType === VIPRLibraries.LIB_TYPE_ALL) {
				this.viprdefinitionsPromises = null;
				this.createDefinitionsMap = {};
				this.loadedVipr3Bundles = {};
			} else if (this.viprdefinitionsPromises) {
				let libIds = [];
				if (libType === VIPRLibraries.LIB_TYPE_SYSTEM) {
					libIds = VIPR.getSystemLibIds();
				} else if (libType === VIPRLibraries.LIB_TYPE_CUSTOM) {
					libIds = VIPR.getCustomLibIds();
				}

				libIds.forEach(id => {
					delete this.viprdefinitionsPromises[id];
					delete this.createDefinitionsMap[id];
					delete this.loadedVipr3Bundles[id];
					VIPRConfig.removeCustomVisConfiguration(id);
				});
			}
		},

		_visualizationPreviewWasLoaded() {
			return !!(this.loadedVipr3Bundles['visualizationPreview']);
		},

		refreshProvider: function(dashboardApi) {
			const visPreviewWasLoaded = this._visualizationPreviewWasLoaded();
			this.clear(VIPRLibraries.LIB_TYPE_CUSTOM);
			this.initPromise = VIPR.initialize({ 'dashboardApi': dashboardApi, refresh:true, ajax:this.ajax, preFetch:this.preFetch });
			return this.initPromise
				.then(this._getVIPRDefinitions.bind(this, VIPRLibraries.LIB_TYPE_CUSTOM, visPreviewWasLoaded))
				.then(() => {
					const promises = Object.values(this.viprdefinitionsPromises);
					return Promise.all(promises)
						.then(viprDefinitions => {
							return _.chain(viprDefinitions)
								.map(viprDef => {
									return viprDef ? this.createDefinition(viprDef, true) : null;
								})
								.filter(def => {
									return !!def;
								})
								.value();
						});
				});
		},

		getDefinition: function(id) {
			return this.initPromise.then(() => {
				return this._getVIPRDefinition(id).then(viprDef => {
					if (viprDef) {
						if (!this.createDefinitionsMap[id]) {
							this.createDefinitionsMap[id] = this.createDefinition(viprDef, true);
						}
						return this.createDefinitionsMap[id];
					} else {
						return Promise.reject(VisUtil.createVisDefinitionLoadingError(id));
					}
				});
			});
		},

		getDefinitions: function(optimize) {
			const libType = optimize ? VIPRLibraries.LIB_TYPE_SYSTEM : VIPRLibraries.LIB_TYPE_ALL;
			return this.initPromise.then(()=>{
				return this._getVIPRDefinitions(libType).then(viprDefinitions => {
					return this._createDefinitions(viprDefinitions, true);
				});
			});
		},

		_createDefinitions: function(viprDefinitions, freezeDefinition) {
			if (!this.createDefinitionsMap) {
				this.createDefinitionsMap = {};
			}
			return _.chain(viprDefinitions)
				.map(def => {
					if (!this.createDefinitionsMap[def.id]) {
						this.createDefinitionsMap[def.id] = this.createDefinition(def, freezeDefinition);
						return this.createDefinitionsMap[def.id];
					}
				})
				.filter(def => {
					return !!def;
				})
				.value();
		},

		/**
		 * re-load the vipr bundle and recreate the definition
		 * @param {string} id the visId
		 */
		refresh(id) {
			VIPR.unloadBundle(id);
			return this._loadVIPRDefinition(id).then(viprDef=>{
				if (!this.createDefinitionsMap) {
					this.createDefinitionsMap = {};
				}
				this.createDefinitionsMap[id] = viprDef ? this.createDefinition(viprDef, true) : { id, renderer: VIPR_RENDERER, empty: true };
				return this.createDefinitionsMap[id];
			});
		},

		_loadVIPRDefinition: function(id) {
			return VIPR.loadBundle(id)
				.then((bundle) => {
					this.loadedVipr3Bundles[id] = bundle;
					return bundle;
				}).catch((err) => {
					console.error(err);
					return undefined;
				});
		},

		_getVIPRDefinition: function(id) {
			// initialize the map
			if (!this.viprdefinitionsPromises) {
				this.viprdefinitionsPromises = {};
			}

			if (!this.viprdefinitionsPromises[id]) {
				this.viprdefinitionsPromises[id] = this._loadVIPRDefinition(id);
			}
			return this.viprdefinitionsPromises[id];
		},

		_getVIPRDefinitions: function(libType = VIPRLibraries.LIB_TYPE_SYSTEM, loadVisPreview = false) {

			let bundleIds;
			if ( loadVisPreview) {
				bundleIds = VIPR.getBundleIds(libType);
			} else {
				// Only load definitions for the visualizationPreview bundle when explicitly requested
				bundleIds = _.without(VIPR.getBundleIds(libType), 'visualizationPreview');
			}

			const promises = _.map(bundleIds, (id) => {
				return this._getVIPRDefinition(id);
			});
			// wait for all to finish and filter out the failed bundles
			return Promise.all(promises.map(p => p.catch( e => e )))
				.then((definitions) => {
					return _.filter(definitions, (def) => {
						return !!def;
					});
				})
				.catch((err) => {
					console.error(err);
				});
		},

		_getViprDefinitionProperties(viprDefinition) {
			return viprDefinition.vizDef.properties;
		},

		_getCaption: function (viprDefinition, viprConfigDefinition, isCustomVis, isSchematic) {
			const schematicPreviewCaption = 'schematicTestCaption';
			const visualizationPreview = 'customVisTestVisualization';
			let caption;
			if (!viprDefinition.userData.name || viprDefinition.userData.name === visualizationPreview) {
				caption = viprConfigDefinition.name;
			} else if (isCustomVis) {
				caption = viprDefinition.userData.name;
				// since schematic metadata like name and icon can be updated after creating bundle, and we can not
				// unregister/register the schematic bundle due to shared bundle-context with other visualization like Map
				// we will separate these metadata info and use vis library information that is set in vipr ConfigDefinition
			} else if (isSchematic && viprDefinition.userData.name !== schematicPreviewCaption) {
				caption = viprConfigDefinition.name;
			} else {
				caption = StringResources.get(viprDefinition.userData.name);
			}
			return caption;
		},

		/**
		* Return a Visualization definition object that can be consumed by VIPR Data and LiveWidget
		**/
		createDefinition: function(viprDefinition, freezeDefinition) {
			const viprConfigDefinition = VIPRConfig.getConfig(viprDefinition.id);
			const type = (viprConfigDefinition && viprConfigDefinition.type) || viprDefinition.userData.type;
			const isSchematic = viprConfigDefinition && viprConfigDefinition.isSchematic;
			const isCustomVis = viprConfigDefinition && viprConfigDefinition.isCustomVis;
			const usages = viprConfigDefinition && viprConfigDefinition.usages;
			const placeholderIcon = this._getPlaceholder(viprDefinition, isCustomVis, isSchematic);
			const caption = this._getCaption(viprDefinition, viprConfigDefinition, isCustomVis, isSchematic);

			let viprProperties = [];
			let viprGroupProperties = {};

			// since schematic metadata like name,icon and assetId can be updated after creating bundle, and we can not
			// unregister/register the schematic bundle due to shared bundle-context with other visualization like Map
			// we will separate these metadata info and use vis library information that is set in vipr ConfigDefinition
			const icon = isSchematic ? viprConfigDefinition.icon : viprDefinition.userData.icon;
			const assetId = isSchematic ? viprConfigDefinition.assetId : viprDefinition.userData.assetId;

			// set VIPRConig for extensible vis based on VIDA properties
			if (VIPRUtils.includeAllProperties(viprDefinition)) {
				this._setViprConfigDefinition(viprConfigDefinition, viprDefinition, viprProperties, viprGroupProperties);
			}

			const vizDefProperties = PropertiesCreatorFromVisDefinition.getInstance().getVizDefPropertiesFromDefinition(viprDefinition);

			let properties = PropertiesCreatorFromVisDefinition.getInstance().getPropertiesFromDefintion(viprDefinition.id);
			if (isCustomVis || isSchematic) {
				this._convertVIPRCustomColorDefaultValues(properties);
			}

			let definition = {
				type: type,
				isSchematic: isSchematic,
				isCustomVis: isCustomVis,
				id: viprDefinition.id,
				supportsSortAction: VIPRUtils.supportsSortAction(viprDefinition.id),
				supportsContextualTopBottomAction: VIPRUtils.supportsContextualTopBottomAction(viprDefinition.id),
				supportsNonContextualTopBottomAction: VIPRUtils.supportsNonContextualTopBottomAction(viprDefinition.id),
				supportsFormatAction: VIPRUtils.supportsFormatAction(viprDefinition.id),
				supportsMaintainAxisScale: VIPRUtils.supportsMaintainAxisScale(viprDefinition.id),
				supportsCustomGroupAction: VIPRUtils.supportsCustomGroupAction(viprDefinition.id),
				supportsCustomDataSelection: VIPRUtils.supportsCustomDataSelection(viprDefinition.id),
				supportsAnnotations: VIPRUtils.supportsAnnotations(viprDefinition.id),
				supportsForecasts: VIPRUtils.doesConfigPropertyMatchExpected(viprDefinition.id, 'supportsForecasts', true),
				datasets: this._getDatasets(viprDefinition.vizDef.dataSets),
				geo:  VIPRUtils.isGeospatial(viprDefinition.id),
				name: viprDefinition.manifest.name,
				caption: caption,
				label: caption,
				icon: icon,
				assetId: assetId,
				tabs: VIPRUtils.getVisualizationTabs(viprDefinition.id),
				placeholderIcon: placeholderIcon,
				isRecommendable: VIPRUtils.isRecommendable(viprDefinition.id),
				noDataQuery: VIPRUtils.noDataQuery(viprDefinition.id),
				isMultilayersWidget: VIPRUtils.doesConfigPropertyMatchExpected(viprDefinition.id, 'isMultilayersWidget', true),
				useFPDAsInsightsInput: VIPRUtils.useFPDAsInsightsInput(viprDefinition.id),
				disablePan: VIPRUtils.isDisablePan(viprDefinition.id),
				renderWithoutCompletingMapping: VIPRUtils.isRenderWithoutCompletingMapping(viprDefinition.id),
				aggregateAndSortOnClient: VIPRUtils.aggregateAndSortOnClient(viprDefinition.id),
				dataSlots: VIPRUtils.createDataSlots(viprDefinition),
				metaDataEntries: VIPRUtils.createMetaDataEntries(viprDefinition.vizDef),
				properties: properties,
				vizDefProperties: vizDefProperties,
				propertyPrefixes: VIPRUtils.getPropertyPrefixes(viprDefinition),
				listenForPropChanges: VIPRUtils.getListenForPropChanges(viprDefinition),
				groupedProperty: VIPRUtils.getGroupedProperty(viprDefinition),
				renderer: VIPR_RENDERER,
				scope: 'default',
				queryHints: VIPRUtils.getQueryHints(viprDefinition.id),
				binningConfig: VIPRUtils.getBinning(viprDefinition.id),
				canApplyBinning: VIPRUtils.canApplyAutoBin(viprDefinition.id),
				isViewBinned: false,
				supportsOLAP: !VIPRUtils.doesConfigPropertyMatchExpected(viprDefinition.id, 'doesNotSupportOLAP', true),
				usages: usages
			};

			if (isSchematic) {
				definition.getSchematicContent = viprConfigDefinition.schematicVis.getSchematicContent.bind(viprConfigDefinition.schematicVis);
			}

			let nLimit = VIPRUtils.getDataRowLimit(viprDefinition.id);
			if(nLimit){
				definition.dataRowLimit = nLimit;
			}
			let nLimitIE = VIPRUtils.getDataRowLimitIE(viprDefinition.id);
			if(nLimitIE){
				definition.dataRowLimitIE = nLimitIE;
			}
			return freezeDefinition ? ObjectHelper.deepFreezeObject(definition) : definition;
		},

		_convertVIPRCustomColorDefaultValues(properties) {
			properties && properties.forEach(property => {
				if (property.type === 'color' && property.defaultValue) {
					// convert VIPR IColor to hex RGB color
					property.defaultValue = property.defaultValue.toString();
				}
			});
			return properties;
		},

		_setViprConfigDefinition(viprConfigDefinition, viprDefinition, viprProperties, viprGroupProperties) {
			VIPRUtils.getViprProperiesAndGroups( viprDefinition, viprProperties, viprGroupProperties);
			let options = {
				viprProperties: viprProperties,
				viprGroupProperties: { ...viprGroupProperties },
				dataSets: viprDefinition.vizDef.dataSets
			};
			VIPRConfig.setAllViprConfigProperties(viprConfigDefinition, options);
			viprDefinition.userData.config = {
				'properties': {
					'exclude': [],
					'include': viprConfigDefinition.bundleInclude
				}
			};
			VIPR.updateCustomLibrary(viprDefinition.id);
		},

		_getPlaceholder(viprDefinition, isCustomVis, isSchematic) {
			const isNonPreviewSchematic = isSchematic && viprDefinition.id !== 'com.ibm.vis.schematicsPreview';
			const isNonPreviewCustom = isCustomVis && viprDefinition.id !== 'visualizationPreview';
			let iconBase = this.icon_root;
			let icon = viprDefinition.userData.placeholderIcon;
			if (viprDefinition.userData.icon) {
				// When there is an icon in custom or schematic definition, returns this icon
				if (isNonPreviewCustom || isNonPreviewSchematic) {
					iconBase = viprDefinition.userData.placeholderIcon;
					icon = viprDefinition.userData.icon;
				}
			} else {
				// When there is no icon in custom or schematic definition, return default placeholderIcon respectively
				if (isCustomVis) {
					icon = 'placeholders/custom-visual.svg';
				} else if (isSchematic) {
					icon = 'placeholders/custom-schematic.svg';
				}
			}
			return iconBase + icon;
		},

		_getDatasets: function(datasets){
			var bIsSingleDataset = datasets.length === 1;
			return _.map(datasets, function(dataset){
				return {
					/* Default Dataset name for sinlge dataset Viz is set to be 'data' if there is no name in the definition */
					'name': bIsSingleDataset && !dataset.name ? 'data' : dataset.name,
					'optional': dataset.optional,
					'suppressMissing': dataset.suppressMissing,
					'needsSorted': dataset.needsSorted,
					'requiresValues': dataset.requiresValues,
					'caption': dataset.caption
				};
			});
		}
	});

	return VIPR3Provider;
});
